import type React from 'react'
import { createContext, useContext } from 'react'
import type { BackButtonConfig, OverflowTabsConfig, PageAction, PageTabsConfig } from './types'

export interface TabCounts {
	[key: string]: number
}

// Shared empties so clearing a header that is already clear changes nothing: a fresh [] or {} would
// re-identify the context value and re-render everything under the provider on every navigation.
export const NO_ACTIONS: PageAction[] = []
export const NO_TAB_COUNTS: TabCounts = {}

/** Every member is supplied by `PageProvider`, so consumers never need to guard a call. */
export interface PageContextType {
	actions: PageAction[]
	/**
	 * Pathname of the page that registered `actions`. The previous page's or tab's actions stay on screen
	 * until the next page registers, sometimes under the same ids, so this tells them apart.
	 */
	actionsPathname: string | null
	setPageActions: (actions: PageAction[], pathname: string | null) => void
	updatePageAction: (actionId: string, updates: Partial<PageAction>) => void
	clearPageActions: () => void
	tabCounts: TabCounts
	setTabCounts: (counts: TabCounts) => void
	/** Tabs a page registers itself, replacing the ones its route would otherwise contribute. */
	dynamicTabs: PageTabsConfig | null
	setPageTabs: (tabs: PageTabsConfig | null) => void
	clearPageTabs: () => void
	backButton: BackButtonConfig | null
	setBackButton: (config: BackButtonConfig | null) => void
	clearBackButton: () => void
	/** Tabs a list page parks in the header's overflow dropdown. */
	overflowTabs: OverflowTabsConfig | null
	setOverflowTabs: React.Dispatch<React.SetStateAction<OverflowTabsConfig | null>>
	hideOverflowTabs: boolean
	setHideOverflowTabs: (hide: boolean) => void
	/**
	 * Element the header exposes beside its CTA slot for a page that has no CTAs and would rather put its
	 * own controls there (see `usePageHeaderControls`). Null until the header has mounted it.
	 *
	 * A DOM slot rather than a node in this context on purpose: the page keeps rendering its controls in its
	 * own subtree and only portals them here, so their state, hooks and effects are untouched by the move.
	 */
	headerControlsSlot: HTMLElement | null
	setHeaderControlsSlot: (element: HTMLElement | null) => void
	/**
	 * Element the toolbar strip exposes under its header row, for the rows a page would otherwise stack
	 * above its content: filter chips, a search and pagination row. Portalling them here puts them inside
	 * the strip, so collapsing it clears everything above the table at once. Null on mobile and until the
	 * strip has mounted it. Same DOM-slot reasoning as `headerControlsSlot`.
	 */
	toolbarExtrasSlot: HTMLElement | null
	setToolbarExtrasSlot: (element: HTMLElement | null) => void
	/** True while `PageHeader` has nothing to render, so the layout can collapse the header strip. */
	isPageHeaderEmpty: boolean
	setIsPageHeaderEmpty: (empty: boolean) => void
	/** Expands the toolbar strip when the user has it collapsed, e.g. so `/` can reveal the search bar inside it. */
	openToolbar: (() => void) | null
	setOpenToolbar: (fn: (() => void) | null) => void
}

// Component-free module keeps this context's identity stable across Fast Refresh;
// co-locating it with a component export makes HMR mint a new context and desync the provider.
export const PageContext = createContext<PageContextType | undefined>(undefined)

export function usePageContext(): PageContextType {
	const context = useContext(PageContext)

	if (context === undefined) {
		throw new Error('usePageContext must be used within a PageProvider')
	}

	return context
}

/**
 * Lets the header clear the page's search bar filters when a tab changes, the one caller of
 * `clearRegularFilters`. The page's search bar registers its clear function and never invokes this;
 * a bar that declares itself embedded does not register at all, so pages without one are a no-op.
 *
 * Kept out of `PageContext` on purpose. Both members are stable for the life of the provider, while
 * the header context re-identifies on every actions or tab-count write, and those land on a timer on
 * the polling pages. Sharing one context made every mounted search bar re-render on each tick for
 * two functions that never change.
 */
export interface ClearFiltersContextType {
	registerClearFilters: (fn: (() => void) | null) => void
	clearRegularFilters: () => void
	/** The page's search bar registers its own focus function; a page without one never registers. */
	registerFocusSearch: (fn: (() => void) | null) => void
	/** Whether this page has a main search bar at all, with no side effect — checked before opening the
	 *  toolbar for it, so `/` on a page with none never reveals an empty strip behind the palette. */
	hasMainSearch: () => boolean
	/** Focuses the registered search bar. Only meaningful after `hasMainSearch()` is true. */
	focusMainSearch: () => void
}

export const ClearFiltersContext = createContext<ClearFiltersContextType | undefined>(undefined)

export function useClearFilters(): ClearFiltersContextType {
	const context = useContext(ClearFiltersContext)

	if (context === undefined) {
		throw new Error('useClearFilters must be used within a PageProvider')
	}

	return context
}
